function getSchemaProperties(node: any): any | undefined {
  return node?.properties || node?.schema?.properties || undefined;
}

function resolveFieldSchemaSimple(rootSchema: any, path: string): any | undefined {
  const segments = path.split('.');
  let current: any = rootSchema;
  for(const segment of segments) {
    const props = getSchemaProperties(current);
    if(!props || !props[segment]) return undefined;
    current = props[segment];
  }
  return current;
}

function parseDateToMs(value: any): number | null {
  if(value instanceof Date && !isNaN(value.getTime())) return value.getTime();
  if(typeof value === 'number' && !isNaN(value)) return value;
  if(typeof value === 'string') {
    if(/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map((v: string) => +v);
      return new Date(y, m - 1, d).getTime();
    }
    const iso = new Date(value);
    if(!isNaN(iso.getTime())) return iso.getTime();
  }
  return null;
}

export function applySchemaTransformation(fieldName: string, value: any, schema: any): any {
  if((!value && value !== 0) || !schema) {
    return value;
  }

  const resolveFieldSchema = (rootSchema: any, path: string): any => {
    // Simple resolver first
    const direct = resolveFieldSchemaSimple(rootSchema, path);
    if(direct) return direct;
    // Fallback: breadth-first search to locate a node whose properties contain the first segment
    const segments = path.split('.');
    const target = segments[0];
    const queue: any[] = [rootSchema];
    let foundNode: any = null;
    while(queue.length) {
      const node = queue.shift();
      if(node && typeof node === 'object') {
        const p = getSchemaProperties(node);
        if(p && p[target]) { foundNode = node; break; }
        for(const key of Object.keys(node)) {
          const val = node[key];
          if(val && typeof val === 'object') queue.push(val);
        }
      }
    }
    if(foundNode) return resolveFieldSchemaSimple(foundNode, path);
    return undefined;
  };

  const fieldSchema = resolveFieldSchema(schema, fieldName);
  
  if(!fieldSchema) {
    // Heuristic fallback for known date fields when schema is not resolved
    const NAME_BASED_INT_FIELDS = new Set(['BirthDay','ArrivalDate','DateOfExpiry','DateOfIssue','DepartureDate','SpouseBirthDay']);
    const MIN_NOW_FIELDS = new Set(['ArrivalDate','DateOfExpiry','DepartureDate']); // must be >= now
    const MAX_NOW_FIELDS = new Set(['DateOfIssue','BirthDay','SpouseBirthDay']);   // must be <= now
    if(typeof value === 'string' && NAME_BASED_INT_FIELDS.has(fieldName)) {
      let parsedMs: number | null = null;
      if(/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-').map((v: string) => +v);
        parsedMs = new Date(y, m - 1, d).getTime();
      } else {
        const iso = new Date(value);
        if(!isNaN(iso.getTime())) parsedMs = iso.getTime();
      }
      if(parsedMs !== null) {
        const start = new Date(parsedMs);
        const selectedStart = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        let out = selectedStart;
        if(selectedStart === todayStart) {
          if(MIN_NOW_FIELDS.has(fieldName)) {
            out = Date.now() + 60000; // ensure >= now with small buffer
          } else if(MAX_NOW_FIELDS.has(fieldName)) {
            out = Date.now(); // ensure <= now
          }
        } else if(MIN_NOW_FIELDS.has(fieldName) && selectedStart < todayStart) {
          // If date-only and in the past but min-now applies, bump to now
          out = Date.now() + 60000;
        }
        
        return out;
      }
    }
    return value;
  }
  const schemaType = Array.isArray(fieldSchema?.type) ? fieldSchema?.type : fieldSchema?.type ? [fieldSchema?.type] : [];
  
  // Robust date parsing: accept Date, ISO strings, or YYYY-MM-DD strings
  const originalValue = value;
  const parsedMs: number | null = parseDateToMs(originalValue);
  if(parsedMs !== null) {
    if(fieldSchema?.minDateMilliFromNow === 0) {
      const selected = new Date(parsedMs);
      const selectedLocalStart = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate()).getTime();
      const now = new Date();
      const todayLocalStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      value = selectedLocalStart === todayLocalStart ? Date.now() + 60000 : selectedLocalStart;
    } else if(fieldSchema?.frontPattern === '^[0-9]{4}-[0-9]{2}-[0-9]{2}$') {
      // For date-only fields without min=now, normalize to start of day
      const d = new Date(parsedMs);
      value = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    } else {
      value = parsedMs;
    }
    
  }
  // Normalize date values when schema expects date-only and has minDateMilliFromNow = 0
  if(typeof value === 'number' && fieldSchema) {
    if(fieldSchema.minDateMilliFromNow === 0) {
      const selected = new Date(value);
      const selectedLocalStart = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate()).getTime();
      const now = new Date();
      const todayLocalStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      // If selected day is today (local), ensure value >= now; else send local start of selected date
      value = selectedLocalStart === todayLocalStart ? Date.now() + 60000 : selectedLocalStart; // add small buffer
      // Safety: if for any reason value is still today but < now, bump to now
      if(value < Date.now() && selectedLocalStart === todayLocalStart) {
        value = Date.now() + 60000;
      }
    }
  }
  

  // Final guard: for minDateMilliFromNow === 0 ensure value is not in the past even if formatting slipped
  if(typeof value === 'number' && fieldSchema?.minDateMilliFromNow === 0 && value < Date.now()) {
    value = Date.now() + 60000;
  }
  

  // Re-apply type enforcement after date normalization to guarantee final type
  if(schemaType.length) {
    if(schemaType.includes('string') && typeof value !== 'string' && value !== undefined && value !== null) {
      value = String(value);
    }
    if(schemaType.length === 1 && schemaType[0] === 'integer') {
      if(typeof value === 'string') {
        if(/^\d+$/.test(value)) {
          value = Number(value);
        } else {
          const parsed = Date.parse(value);
          if(!isNaN(parsed)) value = parsed;
        }
      }
    }
  }
  

  const transformation = fieldSchema?.transformation;

  if (!transformation) {
    return value;
  }

  switch (transformation) {
    case 'uppercase':
        return typeof value === 'string' ? value.toUpperCase() : value;
    case 'lowercase':
        return typeof value === 'string' ? value.toLowerCase() : value;
    default:
      console.warn(`Unknown transformation type: ${transformation} for field: ${fieldName}`);
      return value;
  }
}
